/**
 * What the availability search says about the `category` key it was given —
 * #2764 F9/F10 (PO decision 2026-09-25).
 *
 * The server decides, because only the server can see archived categories: the
 * tenant config this app receives leaves them out by design, so a membership
 * test against it would call an archived category "never existed" and show
 * every room — the opposite of what F10 requires. The decision and the header
 * format live in `backend/src/core/lib/categoryKeyResolution.ts`; this is its
 * decoder. Change the two together.
 *
 * | notice       | the search …                 | the customer sees                               |
 * |--------------|------------------------------|-------------------------------------------------|
 * | `null`       | ran as asked                 | the list, as always                             |
 * | `archived`   | narrowed to a retired one    | an explained empty list, the stored name, a way on |
 * | `unknown`    | ignored the key              | every object, with a discreet note              |
 *
 * **A missing or unreadable header is `null`** — exactly what an older backend
 * sends, and exactly today's behaviour. So this can never make a working search
 * worse; the worst case is the explanation not appearing.
 */

import Logger from "../../redux/helpers/logger";

/** Lower-case, because Axios normalises response header names. */
export const CATEGORY_OUTCOME_HEADER = "x-product-category-outcome";

/** Same shape the tenant config uses for a live category's `langNames`. */
export type CategoryLangNames = Record<string, { title: string }>;

export type CategoryNotice =
  | { kind: "archived"; name: string; langNames: CategoryLangNames }
  | { kind: "unknown" };

/** A search result together with what the server said about its category. */
export interface ObjectSearchResult<T> {
  items: T;
  categoryNotice: CategoryNotice | null;
}

const langNamesOf = (value: unknown): CategoryLangNames => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const out: CategoryLangNames = {};
  for (const [lang, entry] of Object.entries(value as Record<string, unknown>)) {
    const title =
      entry && typeof entry === "object"
        ? (entry as { title?: unknown }).title
        : undefined;
    if (typeof title === "string" && title.trim().length > 0) {
      out[lang] = { title };
    }
  }
  return out;
};

/** Decode the header value; anything unexpected is `null`, never a throw. */
export const parseCategoryNotice = (raw: unknown): CategoryNotice | null => {
  if (typeof raw !== "string" || raw.length === 0) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(decodeURIComponent(raw));
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const { kind, name, langNames } = parsed as Record<string, unknown>;
  if (kind === "unknown") return { kind: "unknown" };
  if (kind === "archived" && typeof name === "string" && name.length > 0) {
    return { kind: "archived", name, langNames: langNamesOf(langNames) };
  }
  return null;
};

/**
 * `transformResponse` for the four availability endpoints: keeps the body as
 * it is and attaches the notice from the response headers.
 */
export const withCategoryNotice = <T>(
  items: T,
  meta: unknown
): ObjectSearchResult<T> => {
  const headers =
    meta && typeof meta === "object"
      ? (meta as { responseHeaders?: Record<string, unknown> }).responseHeaders
      : undefined;
  const raw = headers?.[CATEGORY_OUTCOME_HEADER];
  const categoryNotice = parseCategoryNotice(raw);
  // A header that ARRIVED but could not be read is a defect on one of the two
  // sides, not "nothing to explain" — say so where someone can see it.
  if (categoryNotice === null && typeof raw === "string" && raw.length > 0) {
    Logger.for("categoryOutcome").warn("unreadable category outcome header");
  }
  return { items, categoryNotice };
};

/**
 * The notice to show for one search: the server's answer, or — when the entry
 * link carried a `category` too malformed to send — the same "unknown" note.
 * Both are keys that name no category, and the PO decision gives them one
 * wording; only where they are detected differs.
 */
export const categoryNoticeFor = (
  fromServer: CategoryNotice | null | undefined,
  categoryRejectedOnEntry: boolean
): CategoryNotice | null =>
  fromServer ?? (categoryRejectedOnEntry ? { kind: "unknown" } : null);
